import { useState, useEffect, useRef } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import TruncatedTd from "../layout/TruncatedTd";
import Pagination from "../layout/Pagination";
import PermissionGate from "../layout/PermissionGate";
import { formatDecimal } from "../utils/decimalFormat";
import normalize from "../utils/NormalizeSelect";

const AllTransactions = () => {
  const [data, setData] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [credit, setCredit] = useState("0");
  const [debit, setDebit] = useState("0");
  const [merchant, setMerchant] = useState({});
  const [branch, setBranch] = useState({});
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [search, setSearch] = useState("");
  const [totalBalance, setTotalBalance] = useState("-----");
  const dateRangePickerRef = useRef(null);
  const requestIdRef = useRef(0);
  const token = localStorage.getItem("token");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const fetchTransactions = () => {
    const requestId = ++requestIdRef.current;
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT
        }all-transactions?page=${page}&limit=${limit}&merchantId=${merchant.value === undefined ? "" : merchant.value
        }&branchId=${branch.value === undefined ? "" : branch.value
        }&search=${search}&startDate=${start}&endDate=${end}&credit=${credit}&debit=${debit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        if (res.data.code === 1) {
          if (page > 1) {
            if (res.data.data.length === 0) {
              setPage(page - 1);
            } else {
              setData(res.data.data);
              setCount(res.data.count);
              setIsLoading(false);
            }
          } else {
            setData(res.data.data);
            setCount(res.data.count);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      })
      .catch(console.error);
  };
  const fetchMerchantList = () => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}merchant-list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setMerchants(res.data.data);
        }
      })
      .catch(console.error);
  };
  const fetchBranchList = (merchantId) => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}branch-list/${merchantId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setBranches(res.data.data);
        }
      })
      .catch(console.error);
  };
  const exportData = async () => {
    try {
      setIsBusy(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}export-all-merchant-transactions`,
        {
          merchantId: merchant.value === undefined ? "" : merchant.value,
          branchId: branch.value === undefined ? "" : branch.value,
          startDate: start,
          endDate: end,
          search,
          credit,
          debit,
        },
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = Date.now();
      const filename = `merchant_transaction_${timestamp}.xlsx`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsBusy(false);
    }
  };
  const fetchTotalBalance = () => {
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT
        }merchants-total-balance`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setTotalBalance(res?.data?.data);
        }
      })
      .catch(console.error);
  };
  const reset = () => {
    setPage(1);
    setMerchant({});
    setBranch({});
    setBranches([]);
    setStart("");
    setEnd("");
    setSearch("");
    setCredit("0");
    setDebit("0");
  };
  const openCalendar = () => {
    if (dateRangePickerRef.current) {
      dateRangePickerRef.current.flatpickr.open();
    }
  };
  const statusCard = (status) => {
    let badge;
    switch (status) {
      case "pending":
        badge = (
          <span className="badge text-white bg-warning">{status}</span>
        );
        break;
      case "success":
        badge = (
          <span className="badge text-white bg-success">{status}</span>
        );
        break;
      case "failed":
        badge = (
          <span className="badge text-white bg-danger">{status}</span>
        );
        break;
      case "cancelled":
        badge = (
          <span className="badge text-white bg-danger">{status}</span>
        );
        break;

      default:
        badge = "---";
        break;
    }
    return badge;
  }
  useEffect(() => {
    fetchMerchantList();
    fetchTotalBalance();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line
  }, [page, merchant, branch, start, end, search, credit, debit]);
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line
  }, [merchant, branch, start, end, search, credit, debit]);

  return isLoading === true ? (
    <Loader />
  ) : (
    <div className="d-flex flex-column flex-root">
      {/*begin::Page*/}
      <div className="page d-flex flex-row flex-column-fluid">
        {/*begin::Wrapper*/}
        <div
          className="wrapper d-flex flex-column flex-row-fluid"
          id="kt_wrapper"
        >
          <Header />
          <div className="app-body">
            <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
              {/*begin::Container*/}
              <div
                id="kt_toolbar_container"
                className="container-xxl d-block d-md-flex justify-content-between"
              >
                {/*begin::Page title*/}
                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                  {/*begin::Title*/}
                  <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                    Transaction List
                  </h1>
                  {/*end::Title*/}
                </div>
                {/*end::Page title*/}
                {/*begin::Button*/}
                <PermissionGate
                  required={`view_all_merchants_transaction_list`}
                  fallback={<></>}
                >
                  <div className="d-block d-md-flex gap-2">
                    <span
                      className="btn btn-color-light border border-success border-dashed rounded text-success w-100 d-block d-md-inline mb-3 flex-nowrap text-nowrap"
                    >
                      <i className="ki-duotone ki-wallet text-700 text-warning fs-1">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                      {totalBalance} KWD
                    </span>
                    <button
                      onClick={() => {
                        setCredit((prev) => (prev === "1" ? "0" : "1"));
                      }}
                      className={
                        credit === "1"
                          ? "btn btn-color-light bg-success w-100 d-block d-md-inline mb-3 flex-nowrap text-nowrap"
                          : "btn btn-color-success bg-body w-100 d-block d-md-inline mb-3 flex-nowrap text-nowrap"
                      }
                    >
                      <i className="ki-duotone ki-plus-circle text-700 fs-1">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                      Credit
                    </button>
                    <button
                      onClick={() => {
                        setDebit((prev) => (prev === "1" ? "0" : "1"));
                      }}
                      className={
                        debit === "1"
                          ? "btn btn-color-light bg-danger w-100 d-block d-md-inline mb-3 flex-nowrap text-nowrap"
                          : "btn btn-color-danger bg-body w-100 d-block d-md-inline mb-3 flex-nowrap text-nowrap"
                      }
                    >
                      <i className="ki-duotone ki-minus-circle text-700 fs-1">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                      Debit
                    </button>
                    <button
                      onClick={exportData}
                      className="btn btn-color-primary bg-body w-100 d-block d-md-inline mb-3 flex-nowrap text-nowrap"
                      disabled={isBusy}
                    >
                      <i className="ki-duotone ki-cloud-download fs-1">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                      Export
                    </button>
                    <button
                      onClick={reset}
                      className="btn btn-color-warning bg-body w-100 d-block d-md-inline mb-3 text-nowrap"
                    >
                      <i className="ki-duotone ki-filter text-700 fs-1">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                      Reset
                    </button>
                  </div>
                </PermissionGate>
                {/*end::Button*/}
              </div>
              {/*end::Container*/}
            </div>
            {/*end::Toolbar*/}
            <div>
              {/*begin::Container*/}
              <div className="container-xxl">
                {/*begin::Actions*/}
                <div className="row">
                  <PermissionGate
                    required={`view_all_merchants_transaction_list`}
                    fallback={<></>}
                  >
                    <>
                      <div className="col-md-3 mb-3">
                        {/* begin::Merchant List */}
                        <Select
                          className="react-select-styled react-select-bordered"
                          styles={{ width: "100%" }}
                          classNamePrefix="react-select"
                          value={merchant.value === undefined ? null : merchant}
                          onChange={(e) => {
                            setMerchant(e);
                            fetchBranchList(e?.value);
                          }}
                          options={merchants?.map((item) => {
                            return {
                              value: item?._id,
                              label: item?.merchantName,
                            };
                          })}
                          filterOption={(option, input) => {
                            return normalize(option.label).includes(normalize(input));
                          }}
                          placeholder="Search Merchant"
                        />
                        {/* end::Merchant List */}
                      </div>
                      <div className="col-md-3 mb-3">
                        {/* begin::Branch List */}
                        <Select
                          className="react-select-styled react-select-bordered"
                          classNamePrefix="react-select"
                          value={branch.value === undefined ? null : branch}
                          onChange={(e) => {
                            setBranch(e);
                          }}
                          options={branches?.map((item) => {
                            return {
                              value: item?._id,
                              label: item?.name,
                            };
                          })}
                          filterOption={(option, input) => {
                            return normalize(option.label).includes(normalize(input));
                          }}
                          placeholder="Search Branch"
                        />
                        {/* end::Branch List */}
                      </div>
                      <div className="col-md-3 mb-3">
                        <input
                          type="text"
                          value={search}
                          autoComplete="off"
                          onChange={(e) => {
                            setSearch(e.target.value);
                          }}
                          className="form-control"
                          placeholder="Search by Order Id or Transaction Id"
                        />
                      </div>
                      <div className="col-md-3 mb-3">
                        {/*begin::Daterange*/}
                        <div className="d-flex position-relative">
                          <Flatpickr
                            ref={dateRangePickerRef}
                            value={
                              start !== "" && end !== ""
                                ? [new Date(start), new Date(end)]
                                : null
                            }
                            options={{ dateFormat: "d-m-Y", mode: "range" }}
                            onChange={([start, end]) => {
                              if (
                                start !== undefined &&
                                end !== undefined &&
                                typeof start === "object" &&
                                typeof end === "object"
                              ) {
                                setStart(moment(start).format("YYYY-MM-DD"));
                                setEnd(moment(end).format("YYYY-MM-DD"));
                              }
                            }}
                            className="form-control form-control-lg"
                            placeholder="Order date"
                          />
                          <i
                            onClick={openCalendar}
                            className="bi-duotone bi-calendar fs-5 position-absolute"
                            style={{
                              top: "28%",
                              right: "5%",
                              cursor: "pointer",
                            }}
                          ></i>
                        </div>
                        {/*end::Daterange*/}
                      </div>
                    </>
                  </PermissionGate>
                </div>
                {/*end::Actions*/}
              </div>
              {/*end::Container*/}
            </div>

            {/*begin::Container*/}
            <div
              id="kt_content_container"
              className="d-flex flex-column-fluid align-items-start container-xxl"
            >
              {/*begin::Post*/}
              <div className="content flex-row-fluid" id="kt_content">
                {/*begin::Card*/}
                <PermissionGate
                  required={`view_all_merchants_transaction_list`}
                >
                  <div className="card">
                    {/*begin::Card body*/}
                    <div
                      className="card-body py-4 text-center"
                      id="filterDataWrap"
                    >
                      {/*begin::Table*/}
                      {/*end::Table*/}
                      {data?.length === 0 ? (
                        <div className="text-center w-100">
                          <img
                            className="w-25 mx-auto py-20"
                            src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                            alt="no-content"
                          />
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table align-middle table-row-dashed fs-6 gy-5">
                            <thead>
                              <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                <th className="text-center min-w-100px">
                                  Transaction Id
                                </th>
                                <th className="text-center min-w-100px">
                                  Merchant
                                </th>
                                <th className="text-center min-w-100px">
                                  Branch
                                </th>
                                <th className="text-center min-w-100px">
                                  Order Id
                                </th>
                                <th className="text-center min-w-100px">
                                  Type
                                </th>
                                <th className="text-center min-w-100px">
                                  Status
                                </th>
                                <th className="text-center min-w-100px">
                                  Channel
                                </th>
                                <th className="text-center min-w-100px">
                                  Notes
                                </th>
                                <th className="text-center min-w-100px">
                                  Date
                                </th>
                                <th className="text-center min-w-100px">
                                  Time
                                </th>
                                <th className="text-center min-w-100px">
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody className="text-gray-600 fw-semibold">
                              {data &&
                                data?.map((item, index) => {
                                  return (
                                    <tr className="text-center" key={index}>
                                      <td>{item?.transactionId ?? "---"}</td>
                                      <TruncatedTd length={8}>
                                        {item?.merchantName ?? "---"}
                                      </TruncatedTd>
                                      <TruncatedTd length={8}>
                                        {item?.branchName ?? "---"}
                                      </TruncatedTd>
                                      <td>{item?.orderId ?? "---"}</td>
                                      <TruncatedTd length={8}>
                                        {item?.type?.toUpperCase()}
                                      </TruncatedTd>
                                      <td>{item?.channel ? statusCard(item?.status) : "---"}</td>
                                      <TruncatedTd>{item?.channel ?? "---"}</TruncatedTd>
                                      <TruncatedTd length={15}>
                                        {item?.notes}
                                      </TruncatedTd>
                                      <td>
                                        {moment(item?.createdAt)
                                          .tz(process.env.REACT_APP_TIMEZONE)
                                          .format("Do MMM, YYYY")}
                                      </td>
                                      <td>
                                        {moment(item?.createdAt)
                                          .tz(process.env.REACT_APP_TIMEZONE)
                                          .format("hh:mm A")}
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            item?.type === "credit"
                                              ? "badge text-white bg-success px-2"
                                              : "badge text-white bg-danger px-2"
                                          }
                                        >
                                          {item?.type === "credit"
                                            ? `+${formatDecimal(
                                              item?.amountInt,
                                            )}`
                                            : `-${formatDecimal(
                                              item?.amountInt,
                                            )}`}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {/*begin::Paginate*/}
                      {count && Math.ceil(count / limit) > 1 ? (
                        <Pagination
                          page={page}
                          setPage={setPage}
                          count={count}
                          limit={limit}
                        />
                      ) : (
                        ""
                      )}
                      {/*end::Paginate*/}
                    </div>
                    {/*end::Card body*/}
                  </div>
                </PermissionGate>
                {/*end::Card*/}
              </div>
              {/*end::Post*/}
            </div>
          </div>
          <Footer />
        </div>
        {/*end::Wrapper*/}
      </div>
      {/*end::Page*/}
    </div>
  );
};

export default AllTransactions;